'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/navbar';
import { Footer } from '@/components/footer';
import { Sparkles, Shield, Trophy, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

export default function RootPage() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <>
      <Navbar data-zylo-id="zylo-f0c239e14072" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="35:7-35:17" data-zylo-component="RootPage" />

      {/* Hero Section */}
      <section data-zylo-id="zylo-86f8abfc88c6" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="38:7-71:17" data-zylo-component="RootPage" className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-white to-white pt-20 pb-16 sm:pt-32 sm:pb-24">
        <div data-zylo-id="zylo-9fcb5beb86e3" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="39:9-70:15" data-zylo-component="RootPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-97bbd2beb957" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="40:11-69:24" data-zylo-component="RootPage"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div data-zylo-id="zylo-f06675b2a2be" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="46:13-54:26" data-zylo-component="RootPage" variants={itemVariants} className="mb-6 inline-block">
              <Image data-zylo-id="zylo-cc301bd9bcb7" data-zylo-kind="media" data-zylo-cap="742" data-zylo-file="src/app/page.tsx" data-zylo-loc="47:15-53:17" data-zylo-component="RootPage"
                src="https://storage.googleapis.com/synthcore-1e4da.firebasestorage.app/tenants/EjjAWDWfS9UpNjTkDJK12SiYqkK2/projects/767da515-4294-4041-a3ed-2f7520f5662e/images/f369203f-d5f4-42d1-b1ae-89f9b48b240a.jpg?GoogleAccessId=firebase-adminsdk-ibcqk%40synthcore-1e4da.iam.gserviceaccount.com&Expires=1808305379&Signature=XD1y2iN82GtrasDS349%2Bx92zVuj9f63CWEsv96nnsZlFKQcwD6Od6YBS7xdaUdM2%2FAkzpVRcr6ogscjz0kZwO4eZvi5KBalMiiyG7HApcAtyb18nnM2QwBlpv9jC88bFMXh0KfUhpsT%2B4ULqAFRExI8R5e94j3HlP9HJ4iumIGm5qfwsGc%2FAciTci6tWua%2Bu44h9xQOJ08pqmaHhrlacJ3%2BKPBEfA3T%2BOY5RhEp8kQ1CV1jFfBx4rOU2YwWsG3bvpmdxdZCiECE2XVbSApLnKE0PHYLdyH%2BucIvT9hKygDPVpbcYhCsTSpLCLtqpYebNcJWwaGBbRK6LEOeO%2BbosSg%3D%3D"
                alt="User uploaded image: BKE-Hi.jpg"
                width={600}
                height={200}
                className="rounded-lg"
              />
            </motion.div>

            <motion.h1 data-zylo-id="zylo-81c5d2d59d86" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="56:13-61:25" data-zylo-component="RootPage"
              variants={itemVariants}
              className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl text-foreground"
            >
              Transform Your Car & Home
            </motion.h1>

            <motion.p data-zylo-id="zylo-35039f318597" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="63:13-68:24" data-zylo-component="RootPage"
              variants={itemVariants}
              className="mt-6 text-lg text-muted-foreground sm:text-xl"
            >
              Expert auto detailing and professional home inspections. Two premium services, one trusted partner.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Service Cards - Dual Service Hero */}
      <section data-zylo-id="zylo-90b57aac486f" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="74:7-130:17" data-zylo-component="RootPage" className="container mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <motion.div data-zylo-id="zylo-2f7c22acfc85" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="75:9-129:22" data-zylo-component="RootPage"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 gap-8 md:grid-cols-2"
        >
          {/* Auto Detailing Card */}
          <motion.div data-zylo-id="zylo-6c0229c04ccc" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="83:11-104:24" data-zylo-component="RootPage" variants={itemVariants}>
            <Link data-zylo-id="zylo-0f4257b769c8" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/page.tsx" data-zylo-loc="84:13-103:20" data-zylo-component="RootPage" href="/detailing" className="group block h-full">
              <Card data-zylo-id="zylo-c183b49ac7a8" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="85:15-102:22" data-zylo-component="RootPage" className="relative overflow-hidden h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div data-zylo-id="zylo-f5bc8c71073a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="86:17-94:23" data-zylo-component="RootPage" className="relative h-96 overflow-hidden bg-gray-900">
                  <Image data-zylo-id="zylo-eb5a86784953" data-zylo-kind="media" data-zylo-cap="742" data-zylo-file="src/app/page.tsx" data-zylo-loc="87:19-92:21" data-zylo-component="RootPage"
                    src="/images/generated/hero-luxury-auto-detailing.png"
                    alt="Premium Auto Detailing Service"
                    fill
                    className="object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div data-zylo-id="zylo-e72a56bc90a9" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="93:19-93:101" data-zylo-component="RootPage" className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <CardContent data-zylo-id="zylo-27579a6065aa" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="95:17-101:31" data-zylo-component="RootPage" className="relative z-10 p-6 text-white">
                  <h2 data-zylo-id="zylo-0000765b493b" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="96:19-96:78" data-zylo-component="RootPage" className="text-3xl font-bold mb-2">Auto Detailing</h2>
                  <p data-zylo-id="zylo-861e03eaadfb" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="97:19-97:139" data-zylo-component="RootPage" className="text-white/90 mb-4">Premium car detailing at your location. Bring your vehicle back to showroom shine.</p>
                  <div data-zylo-id="zylo-5d8e628aeb7b" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="98:19-100:25" data-zylo-component="RootPage" className="inline-flex items-center gap-2 text-accent font-semibold">
                    Explore Service <span data-zylo-id="zylo-c0b6613dab36" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="99:37-99:71" data-zylo-component="RootPage" className="text-xl">→</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>

          {/* Home Inspections Card */}
          <motion.div data-zylo-id="zylo-2b25e657fc78" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="107:11-128:24" data-zylo-component="RootPage" variants={itemVariants}>
            <Link data-zylo-id="zylo-048cd25ccd10" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/page.tsx" data-zylo-loc="108:13-127:20" data-zylo-component="RootPage" href="/inspections" className="group block h-full">
              <Card data-zylo-id="zylo-a73d52ce4141" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="109:15-126:22" data-zylo-component="RootPage" className="relative overflow-hidden h-full border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                <div data-zylo-id="zylo-5d0dd5202c79" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="110:17-118:23" data-zylo-component="RootPage" className="relative h-96 overflow-hidden bg-gray-900">
                  <Image data-zylo-id="zylo-cc2ebe0f4c80" data-zylo-kind="media" data-zylo-cap="742" data-zylo-file="src/app/page.tsx" data-zylo-loc="111:19-116:21" data-zylo-component="RootPage"
                    src="/images/generated/hero-professional-home-inspection.png"
                    alt="Professional Home Inspection Service"
                    fill
                    className="object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div data-zylo-id="zylo-91e5198445af" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="117:19-117:101" data-zylo-component="RootPage" className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <CardContent data-zylo-id="zylo-24ab55eec57d" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="119:17-125:31" data-zylo-component="RootPage" className="relative z-10 p-6 text-white">
                  <h2 data-zylo-id="zylo-5b8d91fce3b1" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="120:19-120:80" data-zylo-component="RootPage" className="text-3xl font-bold mb-2">Home Inspections</h2>
                  <p data-zylo-id="zylo-71ac49855799" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="121:19-121:139" data-zylo-component="RootPage" className="text-white/90 mb-4">Comprehensive professional inspections for buying, selling, or insurance purposes.</p>
                  <div data-zylo-id="zylo-8155461f1fe0" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="122:19-124:25" data-zylo-component="RootPage" className="inline-flex items-center gap-2 text-secondary font-semibold">
                    Explore Service <span data-zylo-id="zylo-a07495fead35" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="123:37-123:71" data-zylo-component="RootPage" className="text-xl">→</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Why Choose Blue Kraken */}
      <section data-zylo-id="zylo-511f2b40a1ca" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="133:7-200:17" data-zylo-component="RootPage" className="bg-primary/5 py-16 sm:py-24">
        <div data-zylo-id="zylo-8eab1269d906" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="134:9-199:15" data-zylo-component="RootPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-96c9288ec40d" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="135:11-154:24" data-zylo-component="RootPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-3xl text-center mb-12"
          >
            <motion.h2 data-zylo-id="zylo-7f63cd14a5e1" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="142:13-147:25" data-zylo-component="RootPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl text-foreground mb-4"
            >
              Why Choose Blue Kraken?
            </motion.h2>
            <motion.p data-zylo-id="zylo-538776de150c" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="148:13-153:24" data-zylo-component="RootPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground"
            >
              Two services, one commitment to excellence.
            </motion.p>
          </motion.div>

          <motion.div data-zylo-id="zylo-f907dca940b6" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="156:11-198:24" data-zylo-component="RootPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              {
                icon: Trophy,
                title: "Expert Team",
                description: "Trained professionals with years of experience"
              },
              {
                icon: Sparkles,
                title: "Premium Quality",
                description: "Top-grade materials and meticulous attention to detail"
              },
              {
                icon: Shield,
                title: "Fully Insured",
                description: "Complete protection and peace of mind"
              },
              {
                icon: MapPin,
                title: "Local Service",
                description: "We come to you for your convenience"
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div data-zylo-id="zylo-e67d3094178f" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="187:17-195:30" data-zylo-component="RootPage" key={index} variants={itemVariants}>
                  <Card data-zylo-id="zylo-c11e29a77f5e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="188:19-194:26" data-zylo-component="RootPage" className="h-full border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent data-zylo-id="zylo-605d14568a6b" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="189:21-193:35" data-zylo-component="RootPage" className="p-6">
                      <Icon data-zylo-id="zylo-61c63c3e1fe4" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="190:23-190:70" data-zylo-component="RootPage" className="h-12 w-12 text-accent mb-4" />
                      <h3 data-zylo-id="zylo-ef4e071b5cc0" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="191:23-191:79" data-zylo-component="RootPage" className="text-lg font-bold mb-2">{item.title}</h3>
                      <p data-zylo-id="zylo-b7dbe8f287d4" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="192:23-192:90" data-zylo-component="RootPage" className="text-sm text-muted-foreground">{item.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-zylo-id="zylo-24a0a2b0665b" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="203:7-246:17" data-zylo-component="RootPage" className="bg-gradient-to-r from-primary to-primary/80 py-16 sm:py-24 text-white">
        <div data-zylo-id="zylo-e18237d5ef56" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="204:9-245:15" data-zylo-component="RootPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-60a171d3c739" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="205:11-244:24" data-zylo-component="RootPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-2xl text-center"
          >
            <motion.h2 data-zylo-id="zylo-6f0d0f2b9b8e" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="212:13-217:25" data-zylo-component="RootPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl mb-4"
            >
              Ready to Get Started?
            </motion.h2>
            <motion.p data-zylo-id="zylo-7b752e26f846" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/page.tsx" data-zylo-loc="218:13-223:24" data-zylo-component="RootPage"
              variants={itemVariants}
              className="text-lg text-white/90 mb-8"
            >
              Book your appointment today and experience the Blue Kraken difference.
            </motion.p>
            <motion.div data-zylo-id="zylo-b510590d4ac5" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="224:13-243:26" data-zylo-component="RootPage"
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button data-zylo-id="zylo-dbf7453353af" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="228:15-234:24" data-zylo-component="RootPage"
                size="lg"
                asChild
                className="bg-accent hover:bg-accent/90 text-white"
              >
                <Link data-zylo-id="zylo-15a031b4ac3a" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/page.tsx" data-zylo-loc="233:17-233:67" data-zylo-component="RootPage" href="/detailing">Book Auto Detailing</Link>
              </Button>
              <Button data-zylo-id="zylo-09720e910b08" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="235:15-242:24" data-zylo-component="RootPage"
                size="lg"
                variant="outline"
                asChild
                className="border-white text-white hover:bg-white/10"
              >
                <Link data-zylo-id="zylo-4d9d0b4f4e32" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/page.tsx" data-zylo-loc="241:17-241:70" data-zylo-component="RootPage" href="/inspections">Book Home Inspection</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-zylo-id="zylo-f5eba66c83b5" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/page.tsx" data-zylo-loc="248:7-248:17" data-zylo-component="RootPage" />
    </>
  );
}
